import React from "react";
import { useWatch } from "react-hook-form";
import { Box, Text } from "@chakra-ui/react";

import { useLocale } from "#app/context/LocaleContext.jsx";
import { formatLocalDate, formatMatchDate } from "#app/utils/dateFormat.js";
import { getStayShapeHint } from "#app/utils/stayShape.js";
import { listDays } from "#app/utils/listDays.js";

import { DatePickHint } from "./DatePickHint.jsx";

/**
 * Regular form, under the nights: what the request will watch, or, when the
 * picked days include a likely check-out day, a hint that fixes it. Only one
 * shows at a time, since both describe the same picks. The check-out rules
 * and the data behind them live in utils/stayShape.js.
 */
export function StaySummary({ formContext, analyticsProps }) {
  const locale = useLocale();
  const { control, setValue } = formContext;
  const selectedDates = useWatch({ control, name: "selectedDates" }) || [];
  const nights = Number(useWatch({ control, name: "minStayLength" }));
  const [keptKey, setKeptKey] = React.useState(null);

  if (selectedDates.length === 0 || !(nights >= 1)) return null;

  const hint = getStayShapeHint(selectedDates, nights);
  const key = [
    hint?.kind,
    nights,
    ...selectedDates.map((day) => day.getTime()).sort(),
  ].join(":");

  if (!hint || keptKey === key) {
    const days = listDays(selectedDates, (day) => formatMatchDate(day, locale));
    return (
      <Text fontSize="sm" color="muted" mt={2} data-testid="stay-watch-preview">
        Watching {nights > 1 ? `${nights}+ night stays` : "stays"} arriving{" "}
        {days}.
      </Text>
    );
  }

  // Weekday names are unambiguous here: every shape spans under a week
  const day = (date) => formatLocalDate(date, locale, "EEEE");
  const setDates = (dates) =>
    setValue("selectedDates", dates, {
      shouldDirty: true,
      shouldValidate: true,
    });
  const without = (date) =>
    selectedDates.filter((d) => d.getTime() !== date.getTime());
  const common = {
    kind: hint.kind,
    analyticsProps,
    onKeep: () => setKeptKey(key),
  };

  let content;
  if (hint.kind === "arrival-departure") {
    content = (
      <DatePickHint
        {...common}
        title={`Arriving ${day(hint.arrival)}, leaving ${day(hint.departure)}?`}
        fixLabel={`${day(hint.arrival)}, ${hint.nights} nights`}
        onFix={() => {
          setDates([hint.arrival]);
          setValue("minStayLength", hint.nights, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
        keepLabel="Keep both days"
      >
        Each day you pick is an arrival day. For that stay, pick{" "}
        {day(hint.arrival)} and set {hint.nights} nights.
      </DatePickHint>
    );
  } else {
    const checkout = day(hint.checkoutDay);
    content = (
      <DatePickHint
        {...common}
        title={`Is ${checkout} your check-out day?`}
        fixLabel={`Remove ${checkout}`}
        onFix={() => setDates(without(hint.checkoutDay))}
        keepLabel={`Keep ${checkout}`}
      >
        Each day you pick is an arrival day, so this also watches {checkout}{" "}
        arrivals.
        {hint.kind === "checkout-day" &&
          ` ${day(hint.arrival)} for ${hint.nights} nights already runs to ${checkout}.`}
      </DatePickHint>
    );
  }

  return <Box mt={4}>{content}</Box>;
}
